import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAuditOverview } from '../hooks/useAudit';
import { seoApi } from '../api/seoApi';
import { seoKeys } from '../queryKeys';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';
import { lifecycleApi } from '@/modules/strategy/api/lifecycleApi';
import { strategyKeys } from '@/modules/strategy/queryKeys';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { CTABanner } from '@/shared/ui/CTABanner';
import { PageHeader, Gauge, Stat } from '@/shared/ui';
import { Star, ChevronDown, Sparkles } from 'lucide-react';
import { emojiToIcon } from '@/shared/utils/emojiToIcon';
import styles from './SEODashboardPage.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type VisibilityState = 'abs_zero' | 'zero' | 'strategy';

// ─── Page ─────────────────────────────────────────────────────────────────────

export function OverviewPage() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const navigate = useNavigate();
  const [expandedInsight, setExpandedInsight] = useState<number | null>(null);

  const overviewQuery = useAuditOverview();
  const audit = overviewQuery.data?.latestAudit;

  const gscQuery = useQuery({
    queryKey: integrationKeys.gscStatusShort(locationId),
    queryFn: () => integrationsApi.gsc.getStatus(locationId).then((r) => r.data),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  const visibilityQuery = useQuery({
    queryKey: seoKeys.visibilityOverview(locationId),
    queryFn: () => seoApi.getVisibilityOverview(locationId).then((r) => r.data),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  const lifecycleQuery = useQuery({
    queryKey: strategyKeys.lifecycle(locationId),
    queryFn: () => lifecycleApi.getLifecycle(locationId),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  // State detection
  const gscConnected = gscQuery.data?.connected ?? false;
  // Use `hasActiveStrategy` (any non-archived version is active), not
  // `strategy.status === 'active'` — the latter flips false the moment a draft
  // refresh starts, even though the prior approved version is still active.
  const strategyActive = lifecycleQuery.data?.data?.hasActiveStrategy ?? false;
  // A wizard draft was started but never activated — show "Complete Strategy".
  const strategyInProgress = lifecycleQuery.data?.data?.strategy?.status === 'in_progress';

  const state: VisibilityState = strategyActive ? 'strategy' : gscConnected ? 'zero' : 'abs_zero';

  const score = audit?.overallScore ?? null;
  const summary = audit?.executiveSummary?.owner_friendly_summary ?? null;
  const insightCards = audit?.executiveSummary?.insight_cards ?? [];
  const aiReadiness = audit?.aiReadiness;
  const aeoChecks = audit?.sectionScores?.aeo_ai_visibility?.checks ?? [];
  const aeoPassCount = aeoChecks.filter((c) => c.status === 'pass').length;
  const aeoTotal = aeoChecks.length || 14;

  const gsc = gscQuery.data;
  const vis = visibilityQuery.data;

  // Integration statuses for connection prompts
  const integrations = vis?.integrations ?? {};
  const showTopBanner = !gscConnected && integrations.google_business_profile !== 'connected';
  const missingConnections: Array<{ provider: string; label: string; reason: string }> = [];
  // Only show GSC/GBP here if the top banner isn't already showing them
  if (!showTopBanner && !gscConnected)
    missingConnections.push({
      provider: 'google_search_console',
      label: 'Google Search Console',
      reason: 'track how patients find you',
    });
  if (!showTopBanner && integrations.google_business_profile !== 'connected')
    missingConnections.push({
      provider: 'google_business_profile',
      label: 'Google Business Profile',
      reason: 'manage reviews and profile',
    });
  if (integrations.wordpress !== 'connected' && integrations.webflow !== 'connected')
    missingConnections.push({
      provider: 'cms',
      label: 'Website CMS',
      reason: 'let us fix technical issues',
    });

  const isLoading =
    !locationId || overviewQuery.isLoading || gscQuery.isLoading || lifecycleQuery.isLoading;

  const connectGsc = async () => {
    try {
      const { data } = await integrationsApi.gsc.initiateConnect();
      window.location.href = data.authUrl;
    } catch {
      /* silently fail */
    }
  };
  const connectGbp = async () => {
    try {
      const { data } = await integrationsApi.gbp.initiateConnect();
      window.location.href = data.authUrl;
    } catch {
      /* silently fail */
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Online Visibility"
        subtitle="Your online presence score, key insights, and visibility across search and AI engines"
        timestamp={audit?.createdAt}
      />

      {/* Create Strategy — show when no active strategy */}
      {!isLoading && !strategyActive && (
        <CTABanner
          title="Strategy-Specific Content"
          body="Once you create your marketing strategy, PracticeNova generates an SEO content plan tailored to your growth goals — the right pages, blog posts, and landing pages for the patients you want to attract."
          actions={[
            {
              label: strategyInProgress ? 'Complete Strategy →' : 'Create Strategy →',
              onClick: () => navigate('/strategy/builder'),
              variant: 'primary',
            },
          ]}
        />
      )}

      {/* Connect Your Accounts — show when GSC/GBP not connected */}
      {!isLoading && showTopBanner && (
        <CTABanner
          title="Connect Your Accounts"
          body="We've scanned your website, but we need access to your Google accounts to show you the full picture — how patients find you, your review profile, and keyword rankings."
          actions={[
            { label: 'Connect Google Search Console', onClick: connectGsc, variant: 'primary' },
            { label: 'Connect Google Business Profile', onClick: connectGbp, variant: 'secondary' },
          ]}
        />
      )}

      {isLoading ? (
        <div className={styles.skeletonStack}>
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <>
          {/* Score + Summary Card */}
          <div className={styles.scoreSummaryCard}>
            <Gauge score={score} grade={audit?.grade} size={96} />
            <div className={styles.scoreSummaryBody}>
              <div className={styles.scoreSummaryHeader}>
                <span className={styles.scoreSummaryLabel}>Online Visibility Score</span>
                {state === 'abs_zero' || state === 'zero' ? (
                  <span className={clsx(styles.pill, styles.pillWarning)}>General Scan</span>
                ) : (
                  <span className={clsx(styles.pill, styles.pillSuccess)}>Strategy-Aligned</span>
                )}
              </div>
              {summary && <div className={styles.scoreSummaryText}>{summary}</div>}
            </div>
          </div>

          {/* Stat Cards */}
          <div className={styles.statGrid}>
            {/* Google Search Clicks */}
            <Stat
              card
              label="Google Search Clicks"
              value={
                gscConnected && gsc?.totalClicks != null ? (
                  gsc.totalClicks.toLocaleString()
                ) : (
                  <span onClick={connectGsc} className={styles.statConnect}>
                    Connect GSC to see →
                  </span>
                )
              }
              trend={(() => {
                const cur = gsc?.totalClicks;
                const prev = gsc?.prevTotalClicks;
                if (!gscConnected || cur == null || prev == null || prev <= 0) return undefined;
                const pct = Math.round(((cur - prev) / prev) * 100);
                return pct !== 0 ? (
                  <span
                    className={clsx(
                      styles.statDelta,
                      pct > 0 ? styles.statDeltaUp : styles.statDeltaDown,
                    )}
                  >
                    {pct > 0 ? '+' : ''}
                    {pct}%
                  </span>
                ) : undefined;
              })()}
              sub={gscConnected && gsc?.totalClicks != null ? 'last 28 days' : undefined}
            />

            {/* Google Reviews — attribute the source so a public listing scan
                isn't read as data from a connected profile. */}
            {vis?.reviewCount != null ? (
              <Stat
                card
                label="Google Reviews"
                value={
                  <>
                    {vis.reviewCount} · {vis.reviewRating?.toFixed(1)}{' '}
                    <Star size={16} fill="currentColor" strokeWidth={1.5} aria-hidden />
                  </>
                }
                sub={
                  vis.reviewSource === 'public_scan'
                    ? `from public listing${vis.reviewSourceName ? ` · ${vis.reviewSourceName}` : ''}`
                    : 'from Google'
                }
              />
            ) : (
              <Stat
                card
                label="Google Reviews"
                value="—"
                tone="muted"
                sub={
                  <span onClick={connectGbp} className={styles.statConnect}>
                    Connect Google Business Profile →
                  </span>
                }
              />
            )}

            {/* AI Search Readiness */}
            <Stat
              card
              label="AI Search Readiness"
              value={
                aiReadiness?.overall
                  ? aiReadiness.overall.charAt(0).toUpperCase() + aiReadiness.overall.slice(1)
                  : '—'
              }
              tone={
                aiReadiness?.overall === 'high'
                  ? 'success'
                  : aiReadiness?.overall === 'medium'
                    ? 'warning'
                    : aiReadiness?.overall === 'low'
                      ? 'error'
                      : 'default'
              }
              sub={`${aeoPassCount} of ${aeoTotal} signals in place`}
            />
          </div>

          {/* Key Insights */}
          {insightCards.length > 0 && (
            <div>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>Key Insights</div>
                <button
                  type="button"
                  onClick={() => navigate('/seo/site-health')}
                  className={styles.ghostBtn}
                >
                  All technical details →
                </button>
              </div>
              <div className={styles.insightList}>
                {insightCards.map((ins, i) => {
                  const toneClass =
                    ins.type === 'green'
                      ? styles.insightCardGreen
                      : ins.type === 'red'
                        ? styles.insightCardRed
                        : styles.insightCardYellow;
                  const isExpanded = expandedInsight === i;

                  return (
                    <div
                      key={i}
                      onClick={() => ins.context && setExpandedInsight(isExpanded ? null : i)}
                      className={clsx(
                        styles.insightCard,
                        toneClass,
                        ins.context && styles.insightClickable,
                      )}
                    >
                      <div className={styles.insightRow}>
                        <span className={styles.insightIcon}>
                          {(() => {
                            const Icon = emojiToIcon(ins.icon) ?? Sparkles;
                            return <Icon size={16} strokeWidth={1.75} aria-hidden />;
                          })()}
                        </span>
                        <div className={styles.insightBody}>
                          <div className={styles.insightText}>{ins.text}</div>
                          {isExpanded && ins.context && (
                            <div className={styles.insightContext}>{ins.context}</div>
                          )}
                        </div>
                        {ins.context && (
                          <span
                            className={clsx(styles.chevron, isExpanded && styles.chevronExpanded)}
                          >
                            <ChevronDown size={14} aria-hidden />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Connections Needed */}
          {missingConnections.length > 0 && (
            <div className={styles.connectionsCallout}>
              <div className={styles.connectionsTitle}>Connect accounts to unlock more</div>
              <div className={styles.connectionsRow}>
                {missingConnections.map((conn) => (
                  <button
                    type="button"
                    key={conn.provider}
                    onClick={() => {
                      if (conn.provider === 'google_search_console') connectGsc();
                      else if (conn.provider === 'google_business_profile') connectGbp();
                      else navigate('/settings/organic');
                    }}
                    className={styles.connectionChip}
                  >
                    {conn.label} <span className={styles.connectionReason}>· {conn.reason}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
